import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Check, ChevronsUpDown, ExternalLink, Search, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { useUsers } from '@/shared/users/hooks/useUsers';
import type { User, UserStatus, UserType } from '@/shared/users/types';
import { API_BASE_URL } from '../../api/config';
import { LocalizedNumberDisplay } from '../LocalizedNumberDisplay';
import { resolveCandidateRows, type EntityFilterCandidates } from './filterFacets';

function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getAvatarUrl(user: User): string | undefined {
  if (!user.profile_image_url) return undefined;
  return `${API_BASE_URL}${user.profile_image_url}`;
}

interface UserPickerProps extends EntityFilterCandidates {
  value: string | null;
  onChange: (userId: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  showUserType?: boolean;
  filterUserTypes?: UserType[];
  /**
   * Narrow the options to these account statuses.
   *
   * The companion to `filterUserTypes`, and it exists for the same reason: a
   * field whose server-side predicate names a status set must not offer the rest
   * of the roster. `useUsers()` is deliberately unparameterised — every picker on
   * a page shares that one query — so this narrows client-side rather than
   * sending a `status` param, which would fork the cache key and add a request
   * per picker.
   *
   * An OWNER field is the canonical caller: `require_eligible_deal_owner` and
   * `_validate_transfer_target` both admit only a status in which the subject can
   * still act, so anything else in the list is a choice the server will refuse.
   */
  filterUserStatuses?: UserStatus[];
  /** ALLOW-list: when set, ONLY these users are offered. */
  filterUserIds?: string[];
  /**
   * DENY-list: these users are removed from the options.
   *
   * The counterpart to `filterUserIds`, for the pairing case — "anyone except
   * me", "anyone not already on this team". Passing the excluded ids to
   * `filterUserIds` does the exact opposite (it offers ONLY them), which is a
   * silent inversion the type system can't catch, so the two are separate props
   * rather than one prop with a mode flag.
   */
  excludeUserIds?: string[];
  /** Hide email in trigger button (show only name). Email still visible in dropdown list. */
  compact?: boolean;
  /**
   * Offer a "None" row that clears the selection.
   *
   * Off by default: re-picking the selected user already toggles it off, which is
   * enough inside a create/transition form the user is about to submit anyway. An
   * inline-edit field is where it matters — there "leave nobody assigned" is a real
   * choice, and a toggle nobody can see is not an affordance.
   */
  clearable?: boolean;
  /** Label for the clear row (defaults to "None"). */
  clearLabel?: string;
}

export function UserPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  showUserType = true,
  filterUserTypes,
  filterUserStatuses,
  filterUserIds,
  excludeUserIds,
  compact = false,
  clearable = false,
  clearLabel,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: UserPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const labelId = useId();
  const valueId = useId();
  const resolvedPlaceholder = placeholder ?? t('pickers.selectUser', 'Select user');

  const { data: usersData, isLoading } = useUsers();

  const users = useMemo(() => {
    if (!usersData?.data) return [];
    let filtered = usersData.data;
    if (filterUserTypes?.length) {
      filtered = filtered.filter((u) => filterUserTypes.includes(u.user_type));
    }
    if (filterUserStatuses?.length) {
      filtered = filtered.filter((u) => filterUserStatuses.includes(u.status));
    }
    if (filterUserIds?.length) {
      const idSet = new Set(filterUserIds);
      filtered = filtered.filter((u) => idSet.has(u.id));
    }
    if (excludeUserIds?.length) {
      const excluded = new Set(excludeUserIds);
      filtered = filtered.filter((u) => !excluded.has(u.id));
    }
    return filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [usersData, filterUserTypes, filterUserStatuses, filterUserIds, excludeUserIds]);

  // Keyed off the UNFILTERED fetch, not `users`: a facet row (and the selected
  // value on a deep link) has to render its avatar and email even when the
  // caller's own allow/deny lists would have dropped that user from the options.
  const allUsersById = useMemo(
    () => new Map((usersData?.data ?? []).map((u) => [u.id, u])),
    [usersData]
  );

  const selectedUser = useMemo(
    () => (value ? allUsersById.get(value) || null : null),
    [value, allUsersById]
  );
  const selectedCandidateLabel = value
    ? candidates?.find((candidate) => candidate.id === value)?.label
    : undefined;

  /**
   * Facet mode: offer exactly `candidates`, with the selected value pinned in even
   * when the facet no longer contains it. Both honesty invariants live in
   * `resolveCandidateRows` (see filterFacets.ts).
   *
   * No `search` is passed — this picker's `Command` does its own filtering, and
   * each row below carries the same `value` string the unrestricted rows do, so
   * typing narrows a facet exactly as it narrows the full list.
   */
  const facetRows = useMemo(
    () =>
      candidates
        ? resolveCandidateRows({
            candidates,
            value,
            selectedLabel: selectedUser?.full_name,
          })
        : null,
    [candidates, value, selectedUser]
  );

  const renderUserTypeIndicator = (user: User) => {
    if (!showUserType || user.user_type === 'internal') return null;

    if (user.user_type === 'ai_agent') {
      return (
        <Badge variant="destructive" className="h-4 px-1 text-[10px] gap-0.5">
          <Bot className="h-2.5 w-2.5" />
          {t('userType.ai_agent', 'AI')}
        </Badge>
      );
    }
    if (user.user_type === 'external') {
      return (
        <Badge variant="outline" className="h-4 px-1 text-[10px] gap-0.5">
          <ExternalLink className="h-2.5 w-2.5" />
          {t('userType.external', 'External')}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label id={labelId} required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            // A <Label> can't name a Radix trigger by proximity — without this the
            // control announces only the selected user, with no field name. Both ids
            // are listed so the name stays "{field}, {selected user}": aria-labelledby
            // REPLACES the accessible name, so pointing it at the label alone would
            // drop the value the button already showed.
            aria-labelledby={label ? `${labelId} ${valueId}` : undefined}
            // A `combobox` takes no name from its own contents, so an unlabelled
            // picker would announce as nothing at all — fall back to the placeholder.
            aria-label={label ? undefined : resolvedPlaceholder}
            disabled={disabled || isLoading}
            title={compact && selectedUser?.email ? `${selectedUser.full_name} (${selectedUser.email})` : undefined}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedUser && !selectedCandidateLabel && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedUser ? (
              <span id={valueId} className="flex items-center gap-2 truncate">
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={getAvatarUrl(selectedUser)} alt={selectedUser.full_name} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(selectedUser.first_name, selectedUser.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex flex-col items-start leading-tight min-w-0">
                  <span className="flex items-center gap-1 truncate">
                    <span className="truncate">{selectedUser.full_name}</span>
                    {renderUserTypeIndicator(selectedUser)}
                  </span>
                  {selectedUser.email && !compact && (
                    <span className="text-muted-foreground text-xs truncate max-w-full">
                      {selectedUser.email}
                    </span>
                  )}
                </span>
              </span>
            ) : selectedCandidateLabel ? (
              <span id={valueId} className="truncate">{selectedCandidateLabel}</span>
            ) : (
              <span id={valueId}>{resolvedPlaceholder}</span>
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchUser', 'Search users...')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noUserFound', 'No user found')}</CommandEmpty>
              <CommandGroup>
                {clearable && (
                  <CommandItem
                    // `min-h-11` (44px): this row carries no avatar, so it renders
                    // at CommandItem's bare 32px beside 48px user rows — under the
                    // touch-target floor on the mobile surfaces that offer it (the
                    // Deals and Leads bulk "Assign owner" sheets at 375px).
                    className="min-h-11"
                    value={clearLabel ?? t('app.none')}
                    onSelect={() => {
                      onChange(null);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn('me-2 h-4 w-4 shrink-0', value ? 'opacity-0' : 'opacity-100')}
                    />
                    <span className="text-muted-foreground">{clearLabel ?? t('app.none')}</span>
                  </CommandItem>
                )}
                {(facetRows ?? users.map((u) => ({ id: u.id, label: u.full_name, count: null }))).map(
                  (row) => {
                    // A facet row usually resolves to a real user, so it keeps the
                    // avatar + email the unrestricted rows have; where it does not
                    // (a user outside this caller's fetch), the facet's own label
                    // is all there is and the row degrades to that.
                    const user = allUsersById.get(row.id);
                    const displayName = user?.full_name ?? row.label;
                    return (
                      <CommandItem
                        key={row.id}
                        value={`${displayName} ${user?.email ?? ''}`}
                        onSelect={() => {
                          onChange(row.id === value ? null : row.id);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            'me-2 h-4 w-4 shrink-0',
                            value === row.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Avatar className="h-6 w-6 me-2">
                          {user && <AvatarImage src={getAvatarUrl(user)} alt={displayName} />}
                          <AvatarFallback className="text-xs">
                            {getInitials(user?.first_name, user?.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="truncate">{displayName}</span>
                            {user && renderUserTypeIndicator(user)}
                          </div>
                          {user?.email && (
                            <span className="text-muted-foreground text-xs truncate block">
                              {user.email}
                            </span>
                          )}
                        </div>
                        {row.count !== undefined && row.count !== null && (
                          <span className="ms-2 shrink-0 text-xs tabular-nums text-muted-foreground">
                            <LocalizedNumberDisplay value={row.count} />
                          </span>
                        )}
                      </CommandItem>
                    );
                  }
                )}
              </CommandGroup>
              {/* The escape hatch out of facet mode — see filterFacets.ts, invariant 2. */}
              {facetRows !== null && onShowAllCandidates && (
                <CommandGroup>
                  <CommandItem
                    value="__show_all_candidates__"
                    onSelect={onShowAllCandidates}
                    className="text-muted-foreground touch:h-11"
                  >
                    <Search className="me-2 h-4 w-4 shrink-0" />
                    {showAllCandidatesLabel ?? t('app.showAllOptions')}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

interface MultiUserPickerProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  showUserType?: boolean;
  filterUserTypes?: UserType[];
  maxUsers?: number;
}

export function MultiUserPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  showUserType = true,
  filterUserTypes,
  maxUsers,
}: MultiUserPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectUsers', 'Select users');

  const { data: usersData, isLoading } = useUsers();

  const users = useMemo(() => {
    if (!usersData?.data) return [];
    let filtered = usersData.data;
    if (filterUserTypes?.length) {
      filtered = filtered.filter((u) => filterUserTypes.includes(u.user_type));
    }
    return filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [usersData, filterUserTypes]);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const selectedUsers = useMemo(
    () => value.map((id) => userMap.get(id)).filter(Boolean) as User[],
    [value, userMap]
  );

  const handleSelect = (userId: string) => {
    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId));
    } else if (!maxUsers || value.length < maxUsers) {
      onChange([...value, userId]);
    }
  };

  const handleRemove = (userId: string) => {
    onChange(value.filter((id) => id !== userId));
  };

  const renderUserTypeIndicator = (user: User, small = false) => {
    if (!showUserType || user.user_type === 'internal') return null;

    const sizeClass = small ? 'h-3 px-0.5 text-[8px]' : 'h-4 px-1 text-[10px]';
    const iconSize = small ? 'h-2 w-2' : 'h-2.5 w-2.5';

    if (user.user_type === 'ai_agent') {
      return (
        <Badge variant="destructive" className={cn(sizeClass, 'gap-0.5')}>
          <Bot className={iconSize} />
        </Badge>
      );
    }
    if (user.user_type === 'external') {
      return (
        <Badge variant="outline" className={cn(sizeClass, 'gap-0.5')}>
          <ExternalLink className={iconSize} />
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label required={required}>
            {label}
          </Label>
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            className={cn(
              'justify-between font-normal h-auto min-h-9',
              fullWidth && 'w-full',
              size === 'sm' && 'min-h-8 text-sm',
              !selectedUsers.length && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedUsers.length > 0 ? (
              <div className="flex flex-wrap gap-1 py-0.5">
                {selectedUsers.map((user) => (
                  <Badge
                    key={user.id}
                    variant="outline"
                    className="gap-1 pl-1 pr-0.5 py-0 font-normal bg-muted/50 text-sm"
                  >
                    <Avatar className="h-4 w-4">
                      <AvatarImage src={getAvatarUrl(user)} alt={user.full_name} />
                      <AvatarFallback className="text-[8px]">
                        {getInitials(user.first_name, user.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate max-w-[120px]">{user.full_name}</span>
                    {renderUserTypeIndicator(user, true)}
                    {/* A native button element here would nest inside the one
                        the PopoverTrigger renders, which React rejects as
                        invalid DOM. Same span-as-button shape
                        MultiContactPicker uses. */}
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label={t('app.remove', 'Remove')}
                      className="ms-0.5 rounded-full p-0.5 hover:bg-muted cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(user.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRemove(user.id);
                        }
                      }}
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </Badge>
                ))}
              </div>
            ) : (
              resolvedPlaceholder
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchUser', 'Search users...')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noUserFound', 'No user found')}</CommandEmpty>
              <CommandGroup>
                {users.map((user) => {
                  const isSelected = value.includes(user.id);
                  const isDisabled = !isSelected && !!maxUsers && value.length >= maxUsers;

                  return (
                    <CommandItem
                      key={user.id}
                      value={`${user.full_name} ${user.email}`}
                      onSelect={() => !isDisabled && handleSelect(user.id)}
                      disabled={isDisabled}
                      className={cn(isDisabled && 'opacity-50')}
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <Avatar className="h-6 w-6 me-2">
                        <AvatarImage src={getAvatarUrl(user)} alt={user.full_name} />
                        <AvatarFallback className="text-xs">
                          {getInitials(user.first_name, user.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{user.full_name}</span>
                          {renderUserTypeIndicator(user)}
                        </div>
                        <span className="text-muted-foreground text-xs truncate block">
                          {user.email}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

export function getUserById(users: User[], id: string): User | undefined {
  return users.find((u) => u.id === id);
}
